{#if node}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    data-node={`${node.ids.line}-${node.ids.move}`}
    class={`node ${color}-move ${className}`}
    on:click={onClick}
    on:contextmenu={onContextMenu}
    on:touchstart={onTouchStart}
    on:touchend={onTouchEnd}
    >

    {#if annotationDetails.icon}
      <span class="node-annotation-icon">
        {@html makeSvgIcon({ path: annotationDetails.icon, size: '18' })}
      </span>
    {/if}

    <span
      class='node-highlight-content'
      class:offset-for-annotation-icon={staticAnnotationIconSpacing && !annotationDetails.icon}
      class:limit-begin={idsDeep(node.ids, $nodeLimits.begin)}
      class:limit-focus={idsDeep(node.ids, $nodeLimits.focus)}
      class:limit-end={idsDeep(node.ids, $nodeLimits.end)}
      style={annotationDetails.color ? `color: ${annotationDetails.color}` : null}
    >
      <San san={node.san} {color} {textType} annotation={annotationDetails.inlineText}/>

      {#if tooltipProps}
        <Tooltip {...tooltipProps}/>
      {/if}
    </span>
  </div>
{/if}

<script lang="ts">
import type { MoveListComponent } from 'components/move-list/config/types';
import { getColorAsWord } from 'chessboard/model/game/get-color-as-word';
import { is } from 'src/utils/svelte/assertions';
import Tooltip from 'components/move-list/components/tooltip.svelte';
import { getTooltipProps } from 'components/move-list/helpers/get-tooltip-props';
import San from 'components/move-list/components/san.svelte';
import { makeSvgIcon } from 'chessboard/plugins/effects/helpers/make-svg-icon';
import { getContext } from 'svelte';
import { MoveListPlugin } from 'chessboard/plugins/move-list/config/types';
import { Board } from 'chessboard/model/board/types';
import type { ContextShape } from '../store/types';
import { getPlyAnnotationDetails } from '../helpers/get-ply-annotation-details';

export let className: string = '';
export let node: MoveListComponent.NodeModel;
export let disableAllIcons: boolean = false;
export let staticAnnotationIconSpacing: boolean = false;

const idsDeep = (ids1, ids2) => is(ids1?.line, ids2?.line) && is(ids1?.move, ids2?.move);
const { store, eventHandlers, game } = getContext<ContextShape>(MoveListPlugin.Name);
const {
  annotationsHidden,
  gameMetadata,
  textType,
  translations,
  nodeLimits,
  customColorsEnabled,
  classificationStyle,
  nodeEffectOverrides,
} = store;

let touchTimeout: number | null = null;
$: color = getColorAsWord(node?.color ?? 1);
$: tooltipProps = disableAllIcons
  ? null
  : getTooltipProps({ node, translations: $translations, gameMetadata: $gameMetadata, game });
$: annotationDetails = getPlyAnnotationDetails(node, {
  disableAllIcons,
  disableAnnotations: $annotationsHidden,
  showAllAnnotationsAsText: $classificationStyle === Board.MoveListClassificationStyles.Traditional,
  disableCustomColors: !$customColorsEnabled,
  nodeEffectOverrides: $nodeEffectOverrides,
});

/**
 * on click handler for the node
 */
function onClick(ev: MouseEvent) {
  if (!node?.ids) {
    return;
  }
  eventHandlers.selectNode(node.ids, ev);
}

/**
 * on context menu handler for the node
 */
function onContextMenu(ev: MouseEvent) {
  eventHandlers.openContextMenu(node, ev);
}

/**
 * Handle a touchstart event on mobile devices
 * In any touchstart event, we fire the handle node click event to select the node.
 * If a user holds the touch for half a second, we want to open the context menu
 */
function onTouchStart(e) {
  onClick(e);

  touchTimeout = setTimeout(() => {
    touchTimeout = null;
    onContextMenu(e);
  }, 500);
}

/**
 * Handle a touchend event on mobile devices
 * If a user fires a touch end event before the context menu timeout fires, we'll
 * treat it like a normal 'click' and not show the context menu
 */
function onTouchEnd() {
  if (touchTimeout) {
    clearTimeout(touchTimeout);
  }
}
</script>
